import { useMemo, useRef, useState, type ReactNode } from "react";

import { CircleCheck } from "lucide-react";

import { uploadCatalogImage } from "@/api/functions/catalog-images";
import { ImageUploadField } from "@/components/ui/image-upload-field";
import { DEFAULT_IMAGE_LIMIT, imageUploadFailureMessage } from "@/lib/item-images";
import { useItemImagesUploader } from "@/hooks/item-images-uploader";

import {
  AppDialog,
  AppDialogActions,
  AppDialogContentText,
  AppDialogTitle,
  DialogPrimaryButton,
  DialogSecondaryButton,
} from "@/components/ui/app-dialog";
import { DimensionField } from "@/components/ui/dimension-field";
import { MuiTypography } from "@/components/ui/mui-typography";
import { FormGrid, FormGridCell, type FormGridSpan } from "@/components/ui/form-grid";
import { TreeSelect } from "@/components/ui/tree-select";
import {
  buildCategoryTree,
  categoryAncestorIds,
  filterCategoryTree,
} from "@/lib/catalog-category-tree";
import { LookupSelectField } from "@/components/ui/lookup-select-field";
import { RichTextField } from "@/components/ui/rich-text-field";
import { TextField } from "@/components/ui/text-field";
import { useCatalogLookupMenu, useLookupEditing } from "@/hooks/catalog-admin";
import { useCatalogCategoryTree, useRequestCatalogItem } from "@/hooks/catalog-items";
import { useItemsError } from "@/hooks/items-error";
import { BffUserFacingError, errorMessageForDisplay } from "@/lib/errors";
import {
  blankRequestItemDraft,
  createInputFromDraft,
  requestItemFieldProblems,
  type RequestItemDraft,
} from "@/lib/request-item-form";

// The Request Item dialog — the donor's `RequestItemIsland`, ported.
//
// Source: `timmy/src/domains/items/islands/RequestItemIsland/RequestItemIsland.tsx`.
// The dialog is `maxWidth='lg'` and `fullWidth`, its body is a 12-column MUI
// `Grid` at `spacing={2}`, and the field order below is that grid's, unchanged —
// including the empty `md={4}` cell that pushes the three dimension fields onto
// their own row.
//
// **What this component owns is arrangement.** The rules, the messages and the
// payload are `@/lib/request-item-form` (the donor's react-hook-form `rules` and
// `buildPostData`); the reads and the write are hooks. This file decides which
// control each field wears, when a problem becomes visible, and which of the two
// dialogs is on screen.
//
// **Problems appear as you type, per field.** The donor's form is
// `useForm({ …, mode: 'onChange' })` (`useRequestItemForm.ts`), which means
// react-hook-form validates a field the moment that field changes — so a
// half-typed 51-character model number is an error immediately, while a
// *untouched* blank required field says nothing until submit. `edited` below is
// the "this field has changed" half and `showProblems` is the submit half; a
// message shows when either holds.
//
// **The two failures are notifications, not form copy.** The donor puts both a
// refused submit and a failed image upload in an `ItemsError` snackbar
// (`RequestItemIsland.tsx`), so they go to the app's toaster here. The image one
// never auto-hides, exactly as the donor's `autoHideDuration={null}` says.
//
// **The images block is the donor's last grid cell.** Files go up as they are
// picked and the save carries the ids (ADR-0003, "attach at item save"); the
// first id is the hero, because position is the only thing that carries it
// (ADR-0005 decision 9).

/** The three lookup domains this form reads, and the labels the donor gives them. */
const LOOKUP_LABELS: Record<"manufacturer" | "finish" | "material", string> = {
  manufacturer: "Manufacturer",
  finish: "Color/Finish",
  material: "Material",
};

/** What a successful request tells the Item Search island (#798). */
export interface RequestItemSuccess {
  itemId: number;
  /** The slug of the category the typist submitted, as the create answered it. */
  categorySlug: string | null;
}

export interface RequestItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The category to open on — the Item Search island's band passes the one its
   * results are filtered by (`useRequestItemForm.ts`, the `opening` effect,
   * which presets the field on every opening). The catalog page passes none.
   */
  initialCategoryId?: number | null;
  /**
   * Told once, when the create succeeds, alongside the success dialog — the
   * donor's `requestItemSuccessAction({ itemId, categorySlug })`, which only the
   * Item Search island observes (it appends the new item's card). The catalog
   * page passes none.
   */
  onSuccess?: (result: RequestItemSuccess) => void;
}

/** A blank draft, opened on the preset category when there is one. */
function openingDraft(initialCategoryId: number | null | undefined): RequestItemDraft {
  return { ...blankRequestItemDraft(), categoryId: initialCategoryId ?? null };
}

export function RequestItemDialog({
  open,
  onOpenChange,
  initialCategoryId,
  onSuccess,
}: RequestItemDialogProps) {
  const [draft, setDraft] = useState<RequestItemDraft>(() => openingDraft(initialCategoryId));
  // The opening the draft was last prepared for. The donor presets on every
  // opening (and resets on every close, which `changeOpen` below does), so the
  // preset is applied at the closed → open transition — during render, as the
  // item page resets its variant pick, so the field never paints blank first.
  const [preparedOpen, setPreparedOpen] = useState(open);
  if (open !== preparedOpen) {
    setPreparedOpen(open);
    if (open) setDraft(openingDraft(initialCategoryId));
  }
  const [showProblems, setShowProblems] = useState(false);
  /** Which fields the typist has changed — react-hook-form's `dirtyFields`,
   * which is what `mode: 'onChange'` keys its per-field messages off. */
  const [edited, setEdited] = useState<ReadonlySet<keyof RequestItemDraft>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  const manufacturers = useCatalogLookupMenu("manufacturer");
  const finishes = useCatalogLookupMenu("finish");
  const materials = useCatalogLookupMenu("material");
  const categories = useCatalogCategoryTree();
  const [categorySearch, setCategorySearch] = useState("");
  const request = useRequestCatalogItem();
  const images = useItemImagesUploader({ upload: uploadCatalogImage });
  const { createOption, renameOption, deleteOption } = useLookupEditing();

  // The wire answers the tree flat-and-preorder; the control draws nesting, and
  // the narrowing is local because Atlas answers the whole alive tree in one
  // call where the donor paged it. Same three calls the filter sidebar makes.
  const categoryNodes = useMemo(() => categories.data?.nodes ?? [], [categories.data]);
  const categoryTree = useMemo(
    () => filterCategoryTree(buildCategoryTree(categoryNodes), categorySearch),
    [categoryNodes, categorySearch],
  );
  // Opening on a chosen category reveals it rather than folding it away three
  // levels down — the donor's `expandedItems` on open.
  const categoryAncestors = useMemo(
    () => (draft.categoryId === null ? [] : categoryAncestorIds(categoryNodes, draft.categoryId)),
    [categoryNodes, draft.categoryId],
  );

  /**
   * Which draft is on screen — bumped on every close.
   *
   * The selects guard their own late writes against their own close, and today
   * that is what actually catches a lookup create resolving into a dismissed
   * dialog. This is the backstop under it, and it counts drafts rather than
   * asking "is the dialog open?", because by the time a slow answer arrives the
   * typist may well have reopened: open, yes — the same form, no. Every write
   * into the draft goes through `set`, so one check there covers all of them.
   */
  const draftEra = useRef(0);

  /**
   * Close, and forget the draft — the donor resets its form on every open
   * (`useRequestItemForm`'s `opening` effect).
   *
   * Every close arrives here: Cancel, "Got it", the titlebar's X, Escape and the
   * overlay all reach it through Radix's `onOpenChange`. Doing the reset on the
   * way out rather than in an effect on `open` keeps it a single render, and
   * keeps a reopened dialog from flashing the previous draft.
   */
  const changeOpen = (next: boolean) => {
    if (!next) {
      draftEra.current += 1;
      setDraft(blankRequestItemDraft());
      setShowProblems(false);
      setSubmitted(false);
      setEdited(new Set());
      request.reset();
      images.reset();
    }
    onOpenChange(next);
  };

  // Deliberately not memoized: each render's setters capture *that* render's
  // era, which is the whole mechanism. A stable identity would hand every draft
  // the same closure and there would be nothing to compare against.
  const set = <K extends keyof RequestItemDraft>(key: K) => {
    // Captured here, where the setter is *made* — during a render of this
    // draft. Reading it inside the callback instead would compare the era to
    // itself and guard nothing.
    const era = draftEra.current;
    return (next: RequestItemDraft[K]) => {
      // A write that lands after this draft was put away belongs to a form that
      // no longer exists; taking it would leave the next one blank except for
      // one mysteriously filled field.
      if (draftEra.current !== era) return;
      setDraft((current) => ({ ...current, [key]: next }));
      setEdited((current) => (current.has(key) ? current : new Set([...current, key])));
    };
  };

  const problems = requestItemFieldProblems(draft);
  const problem = (key: keyof RequestItemDraft) =>
    showProblems || edited.has(key) ? problems[key] : undefined;

  // The donor's fields disable themselves while the form is submitting
  // (`InputField.tsx`: `disabled={disabled || isSubmitting}`), and the reason is
  // a race, not politeness: a draft edited after the payload has gone would
  // leave the typist reading a success or a refusal about values they can no
  // longer see.
  const busy = request.isPending;

  /**
   * The donor's `disabled={isImageUploading || imageUploadErrors.length > 0 || isLoading}`.
   *
   * Both image conditions are about a payload that would be wrong rather than
   * merely early: a submit that beat the upload would save an item whose
   * pictures never got attached, and one made with a failure outstanding would
   * silently drop the file the typist thinks they added.
   *
   * `hasFailure` rather than the donor's `imageUploadErrors.length > 0`: the
   * donor clears that list on the next drop, so one more file would re-enable
   * submit with the failed thumbnail still on screen. See the hook.
   */
  const blocked = busy || images.isUploading || images.hasFailure;

  const submit = () => {
    setShowProblems(true);
    if (Object.keys(problems).length > 0) return;
    request.mutate(createInputFromDraft(draft, images.attachedIds), {
      onSuccess: (created) => {
        setSubmitted(true);
        // From the create's answer, which is the item as saved: the slug of the
        // category the typist submitted, whatever the preset was.
        onSuccess?.({
          itemId: created.item.id,
          categorySlug: created.item.category?.slug ?? null,
        });
      },
    });
  };

  // The donor's two `ItemsError` snackbars, through the shared hook — which is
  // where the corner, the weight, the 32px inset and the ten seconds are stated.
  // Both were spelling those out here, and two of the four were missing the
  // weight and the inset.
  useItemsError(request.isError, submitErrorMessage(request.error));

  const failure = images.failures[0];
  useItemsError(
    open && failure !== undefined,
    failure === undefined ? "" : imageUploadFailureMessage(failure),
    // `autoHideDuration={null}` in the donor: there is something to *do* about
    // this one, so it stays until the image is removed.
    { duration: Infinity },
  );

  /**
   * The inline "Add {label}" row's write, and the rename's, as the select's
   * promise seam.
   *
   * Both answer with the row itself (`lookupWire`), which is exactly what the
   * select needs to select — so there is nothing to look up and nothing to
   * reconcile here. The hooks invalidate the kind's list on success, so the
   * options behind the menu catch up on their own.
   */
  if (submitted) {
    return <RequestSubmittedDialog open={open} onClose={() => changeOpen(false)} />;
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={changeOpen}
      title="Request an item"
      size="lg"
      actions={
        <AppDialogActions>
          <DialogSecondaryButton onClick={() => changeOpen(false)}>Cancel</DialogSecondaryButton>
          {/* The donor's `loading={isLoading}` — MUI's loading button pins a
              20px indicator to the left edge and disables itself. */}
          <DialogPrimaryButton
            type="submit"
            form={FORM_ID}
            loading={busy || images.isUploading}
            disabled={blocked}
          >
            Request Item
          </DialogPrimaryButton>
        </AppDialogActions>
      }
    >
      {/* **No bottom margin, because the donor has none.**
          `DialogContentText` is a bare `Typography body1` — a `styled(...)({})`
          with an empty style object (`DialogContentText.js:34-59`) over
          `Typography`'s `margin: 0`, passed no `sx` and no `gutterBottom`
          (`RequestItemIsland.tsx:79-81`) — and the donor's `Grid` is
          `@modsy/react-ui`'s translation of the legacy API onto MUI v9
          (`react-ui/src/grid/grid.tsx:22-40`), where `spacing` compiles to CSS
          `gap`, which never applies above the first row. So the donor's first
          field sits flush under this paragraph, and so does this one: the top
          border lands at y=184 at `lg`, which is what #407's evidence pair
          measured on modsy against Atlas's 200.

          This carried a `mb-4` until #492, and taking it out alone had been
          tried and reverted once: at a flush gap the shrunk outlined label's
          own 1.6px of drift — `leading-none` on a 12px box, where MUI's is the
          scaled 17.25px one — was the difference between the donor's label
          clearing this paragraph's descenders and Atlas's touching them. Both
          halves shipped together in #492; the label half is
          `outlined-field.tsx`, and it is app-wide. */}
      <AppDialogContentText>
        Couldn&apos;t find the item you needed? That&apos;s because it is not in the Lennar Item
        Master. Fill out the information below and we will let you know when the item has been
        approved.
      </AppDialogContentText>

      <FormGrid spacing={2} asChild>
        <form
          id={FORM_ID}
          data-slot="request-item-form"
          noValidate
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Cell field="name" span="full">
            <TextField
              disabled={busy}
              label="Item Name"
              required
              // The donor focuses the first input once the dialog has finished
              // opening (`slotProps.transition.onEntered = focusFirstInput`).
              autoFocus
              value={draft.name}
              onChange={set("name")}
              error={problem("name")}
            />
          </Cell>

          <Cell field="manufacturerId" span="half">
            <LookupSelectField
              disabled={busy}
              label={LOOKUP_LABELS.manufacturer}
              required
              editable
              {...manufacturers}
              value={draft.manufacturerId}
              onChange={set("manufacturerId")}
              error={problem("manufacturerId")}
              onCreateOption={createOption("manufacturer")}
              onRenameOption={renameOption("manufacturer")}
              onDeleteOption={deleteOption("manufacturer")}
            />
          </Cell>

          <Cell field="categoryId" span="half">
            {/* The same shared control the list's filter sidebar draws — the
              donor builds both from `CategoriesFieldPaginated`. The form's
              variant is the donor's default: no empty row (`allowEmpty` is
              `false` unless a caller asks), MUI's default 56px size, and a
              required field's asterisk and error text. */}
            <TreeSelect
              disabled={busy}
              label="Category"
              required
              allowEmpty={false}
              size="medium"
              nodes={categoryTree}
              emptyLabel=""
              searchPlaceholder="Search categories"
              searchTerm={categorySearch}
              onSearchTermChange={setCategorySearch}
              expandedByDefault={categoryAncestors}
              loading={categories.isPending}
              value={draft.categoryId}
              onChange={set("categoryId")}
              error={problem("categoryId")}
            />
          </Cell>

          <Cell field="modelNumber" span="third">
            <TextField
              disabled={busy}
              label="Model Number"
              value={draft.modelNumber}
              onChange={set("modelNumber")}
              error={problem("modelNumber")}
            />
          </Cell>

          <Cell field="jdeItemNumber" span="third">
            <TextField
              disabled={busy}
              label="Item Number (JDE)"
              value={draft.jdeItemNumber}
              onChange={set("jdeItemNumber")}
            />
          </Cell>

          <Cell field="propId" span="third">
            <TextField
              disabled={busy}
              label="Prop ID"
              value={draft.propId}
              onChange={set("propId")}
              error={problem("propId")}
            />
          </Cell>

          {/* `isEditable={false}` on both in the donor: a finish or a material may
            be added from here but not renamed from here. */}
          <Cell field="finishId" span="third">
            <LookupSelectField
              disabled={busy}
              label={LOOKUP_LABELS.finish}
              required
              {...finishes}
              value={draft.finishId}
              onChange={set("finishId")}
              error={problem("finishId")}
              onCreateOption={createOption("finish")}
            />
          </Cell>

          <Cell field="materialId" span="third">
            <LookupSelectField
              disabled={busy}
              label={LOOKUP_LABELS.material}
              {...materials}
              value={draft.materialId}
              onChange={set("materialId")}
              error={problem("materialId")}
              onCreateOption={createOption("material")}
            />
          </Cell>

          {/* The donor's empty `md={4}` cell. It is what puts Width / Height /
            Depth on a row of their own instead of trailing the Material select. */}
          <div aria-hidden="true" className="hidden md:col-span-4 md:block" />

          <Cell field="dimensionWidth" span="third">
            <DimensionField
              disabled={busy}
              label="Width (inches)"
              value={draft.dimensionWidth}
              onChange={set("dimensionWidth")}
              error={problem("dimensionWidth")}
            />
          </Cell>

          <Cell field="dimensionHeight" span="third">
            <DimensionField
              disabled={busy}
              label="Height (inches)"
              value={draft.dimensionHeight}
              onChange={set("dimensionHeight")}
              error={problem("dimensionHeight")}
            />
          </Cell>

          <Cell field="dimensionDepth" span="third">
            <DimensionField
              disabled={busy}
              label="Depth (inches)"
              value={draft.dimensionDepth}
              onChange={set("dimensionDepth")}
              error={problem("dimensionDepth")}
            />
          </Cell>

          <Cell field="url" span="full">
            <TextField
              disabled={busy}
              label="Link to Item"
              value={draft.url}
              onChange={set("url")}
              error={problem("url")}
            />
          </Cell>

          <Cell field="description" span="full">
            <RichTextField
              disabled={busy}
              label="Description"
              value={draft.description}
              onChange={set("description")}
            />
          </Cell>

          {/* `<Grid item xs={12} sx={{ mt: 2 }}>` in the donor
            (`RequestItemIsland.tsx:185`) — the 16px above it is the only thing
            that separates this block from the form. MUI's `2` is 16px, so it is
            `mt-4` here; `mt-2` was 8px and shipped half the gap on both hosts. */}
          <Cell field="images" span="full" className="mt-4">
            <ImageUploadField
              disabled={busy}
              label={`Item Images (Upload up to ${DEFAULT_IMAGE_LIMIT} images)`}
              value={images.images}
              onChange={images.setImages}
              onFilesPicked={images.addFiles}
              onRemove={images.removeAt}
              progress={images.progress}
              queued={images.queued}
            />
          </Cell>
        </form>
      </FormGrid>
    </AppDialog>
  );
}

/**
 * What a refused submit says.
 *
 * Python's item rules raise `invalid-input:catalog-item`, whose message names
 * the field it refused ("Model Number must be 50 characters or fewer.") and
 * which the scrub allowlist passes through intact — so showing it is strictly
 * more use than the donor's fixed sentence, and it is what #252 asks for
 * ("submit failures surface field-level feedback via tagged invalid-input
 * errors"). Anything *not* tagged has already been scrubbed to a generic
 * message by the global middleware, and there the donor's copy is the better
 * thing to say, because it tells the typist what to do next.
 */
function submitErrorMessage(error: unknown): string {
  if (error instanceof BffUserFacingError) return errorMessageForDisplay(error);
  return "There was an error submitting your request. Click on the 'Request Item' button to try again.";
}

/** The form's id, so the submit button can sit in the dialog's action row and
 * still submit the form — which is what the donor's `form='asset-dialog-form'`
 * does. */
const FORM_ID = "request-item-form";

/**
 * One grid cell. `data-field` is what pins the donor's field order in the test.
 *
 * `"images"` is not a draft field — the uploader owns its own state, because the
 * files are already in the bucket by the time the form is submitted — but it is
 * a cell of the donor's grid and its position in that grid is worth pinning
 * alongside the rest.
 */
function Cell({
  field,
  span,
  className,
  children,
}: {
  field: keyof RequestItemDraft | "images";
  span: FormGridSpan;
  className?: string;
  children: ReactNode;
}) {
  return (
    <FormGridCell slot="request-item-field" field={field} span={span} className={className}>
      {children}
    </FormGridCell>
  );
}

/**
 * The donor's `RequestSubmittedDialog` — `maxWidth='sm'`, no titlebar, a 64px
 * success check, a centred `h2`, three paragraphs, and one "Got it".
 */
function RequestSubmittedDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <AppDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      titleInBody
      size="sm"
      actions={
        <AppDialogActions>
          <DialogSecondaryButton onClick={onClose}>Got it</DialogSecondaryButton>
        </AppDialogActions>
      }
    >
      <div className="flex flex-col items-center gap-6 py-6">
        <CircleCheck className="text-lennar-success size-16" aria-hidden="true" />
        <AppDialogTitle className="text-center">Request submitted!</AppDialogTitle>
        <div className="text-center">
          <MuiTypography color="primary" gutterBottom>
            We&apos;ve received your item request.
          </MuiTypography>
          <MuiTypography color="primary" gutterBottom>
            Right now the item is on &quot;Pending Approval&quot; upon request completion. Once the
            item has been approved we will create the 3D model and you&apos;ll be able to use it in
            your home models.
          </MuiTypography>
          <MuiTypography color="primary">
            Until then, you will be able to add this item to your specification in a
            &quot;pending&quot; state.
          </MuiTypography>
        </div>
      </div>
    </AppDialog>
  );
}
